import type { Descendant, TElement, Value } from 'platejs';
import { TextApi } from 'platejs';

const serializeNode = (node: TElement | Descendant): string => {
  if (TextApi.isText(node)) {
    let x: string = node.text;

    if ('bold' in node && node.bold) {
      x = `<strong>${x}</strong>`;
    }

    if ('italic' in node && node.italic) {
      x = `<i>${x}</i>`;
    }

    if ('underline' in node && node.underline) {
      x = `<u>${x}</u>`;
    }

    return x;
  }

  const childrenHtml = node.children.map(serializeNode).join('');

  function indented(node: TElement): string {
    let s: string = '';
    if ('indent' in node && node.indent) {
      const px = (node.indent as number) * 24;
      s = `margin-left: ${px}px;`;
    }
    return s;
  }

  let c: string = '';
  const style = indented(node);

  switch (node.type) {
    case 'p': {
      if ('indent' in node) {
        c = `m-0 px-0 py-1`;
      }

      if ('listStyleType' in node && node.listStyleType === 'disc') {
        return `
          <ul
            class="${c}"
            style="list-style-type: disc; margin: 0px; padding: 0px; position: relative; ${style}"
          >
            <li>${childrenHtml}</li>
          </ul>
        `;
      }

      if ('listStyleType' in node && node.listStyleType === 'decimal') {
        return `
          <ol class="${c}" style="list-style-type: decimal; margin: 0px; padding: 0px; position: relative; ${style}">
            <li>${childrenHtml}</li>
          </ol>
        `;
      }

      return `
        <p
          class="${c}"
          style="${style}">
            ${childrenHtml && childrenHtml.length ? childrenHtml : ''}
        </p>`;
    }

    case 'a': {
      // TODO: link style as in the chat
      const c = 'font-medium underline underline-offset-4';
      // "<a href=\"{redirect_url}\" data-render-type=\"button\" rel=\"noopener noreferrer\" target=\"_blank\">Vidare till Mitt Hubert</a>"
      // target="${node.target}"
      let url = '';
      if (node.url) {
        url = (node.url as string).replace('%7B', '{').replace('%7D', '}');
      }

      // if (node.renderType === 'button') {
      //   c = 'bg-primary-500 rounded-lg text-white py-2 px-4';
      // }

      return `
        <a
          href="${url}"
          class="${c}"
          target="_blank"
          rel="noopener noreferrer"
          ${node.renderType === 'button' ? 'data-render-type="button"' : ''}
          style="${style}"
        >
          ${childrenHtml}
        </a>
      `;
    }

    case 'video': {
      const c = 'chat-bubble-fill';
      const style = 'max-width: 100%; min-height: 315px';
      return `
        <iframe
          class="${c}"
          style="${style}"
          src="${node.url}"
          id="${node.id}"
          title='YouTube video player'
          frameborder='0'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerpolicy='strict-origin-when-cross-origin'
          allowfullscreen
        >
          ${childrenHtml}
        </iframe>
      `;
    }

    case 'img': {
      const c = '';
      const style = '';
      return `
        <img
          class="${c}"
          style="${style}"
          src="${node.url}"
          id="${node.id}"
          alt='Image'
        >
          ${childrenHtml}
        </img>
      `;
    }

    // Ordered list
    case 'ol': {
      return `
        <ol
          style="${style}"
          class="m-0 ps-6 list-decimal slate-ol"
        >
          ${childrenHtml}
        </ol>
      `;
    }
    // Un-ordered list
    case 'ul': {
      return `
        <ul
          style="${style}"
          class="m-0 ps-6 list-disc [&_ul]:list-[circle] [&_ul_ul]:list-[square] slate-ul"
        >
          ${childrenHtml}
        </ul>
      `;
    }
    // List item
    case 'li': {
      return `
        <li
          style="${style}"
          class="slate-li"
        >
          ${childrenHtml}
        </li>
      `;
    }
    // List item "c"
    case 'lic': {
      return `${childrenHtml}`;
    }

    case 'h1': {
      return `
        <h1
          style="${style}"
        >
          ${childrenHtml}
        </h1>`;
    }

    case 'h2': {
      return `
        <h2
          style="${style}"
        >
          ${childrenHtml}
        </h2>`;
    }

    case 'h3': {
      return `
        <h3
          style="${style}"
        >
          ${childrenHtml}
        </h3>`;
    }

    case 'h4': {
      return `
        <h4
          style="${style}"
        >
          ${childrenHtml}
        </h4>`;
    }

    case 'h5': {
      return `
        <h5
          style="${style}"
        >
          ${childrenHtml}
        </h5>
      `;
    }

    case 'h6': {
      return `
        <h6
          style="${style}"
        >
          ${childrenHtml}
        </h6>`;
    }

    // Add more cases as needed based on your node types (headings, lists, etc.)
    default: {
      return childrenHtml; // For unknown types or inline elements
    }
  }
};

export function plateHtmlSerializer(nodes: Value): string {
  return nodes.map(serializeNode).join('').replace(/\s+/g, ' ').trim().replace('\n', '').replace('\t', '');
}
